import React from 'react';

interface ResultsDisplayProps {
  result: any;
}

export function ResultsDisplay({ result }: ResultsDisplayProps) {
  if (!result) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        <p className="text-sm">Submit a request to see results here</p>
      </div>
    );
  }

  const hasError = result.error;
  const hasSuccess = result.requestId && result.status;

  return (
    <div className="space-y-4">
      {/* Results Container */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            {hasError ? (
              <>
                <span className="text-2xl">❌</span>
                Request Failed
              </>
            ) : (
              <>
                <span className="text-2xl">✅</span>
                Response
              </>
            )}
          </h3>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Error Display */}
          {hasError && (
            <div className="bg-red-50 dark:bg-red-900 dark:bg-opacity-30 border border-red-200 dark:border-red-700 rounded-lg p-4">
              <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">Error</p>
              <p className="text-sm text-red-700 dark:text-red-400 font-mono break-words">{result.error}</p>
            </div>
          )}

          {/* Success Summary */}
          {hasSuccess && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {/* Request ID */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">Request ID</p>
                <p className="text-lg font-mono font-bold text-gray-900 dark:text-white">
                  {result.requestId}
                </p>
              </div>

              {/* Status */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">Status</p>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                      result.status === 'DONE'
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : result.status === 'PENDING'
                          ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                          : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                    }`}
                  >
                    {result.status}
                  </span>
                </div>
              </div>

              {/* Submitted At */}
              {result.submittedAt && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-1">Submitted</p>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {new Date(result.submittedAt).toLocaleTimeString()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Collapsible Full Response */}
          <CollapsibleJsonSection title="Full Response" content={result} />
        </div>
      </div>

      {/* Poll URL Info */}
      {result.pollUrl && (
        <div className="bg-blue-50 dark:bg-blue-900 dark:bg-opacity-30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
          <p className="text-xs text-blue-800 dark:text-blue-300">
            <span className="font-medium">💡 Note:</span> You can poll the request at{' '}
            <code className="bg-blue-100 dark:bg-blue-900 px-1 py-0.5 rounded text-xs">
              {result.pollUrl}
            </code>
          </p>
        </div>
      )}
    </div>
  );
}

interface CollapsibleJsonSectionProps {
  title: string;
  content: any;
  icon?: string;
}

function CollapsibleJsonSection({
  title,
  content,
  icon = '📋'
}: CollapsibleJsonSectionProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(content, null, 2));
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left bg-gray-50 dark:bg-gray-700"
      >
        <span className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
          <span>{icon}</span>
          {title}
        </span>
        <span className={`text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {isOpen && (
        <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-900 space-y-3">
          <div className="relative">
            <pre className="text-xs font-mono text-gray-900 dark:text-gray-100 overflow-x-auto max-h-96 overflow-y-auto p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
              {JSON.stringify(content, null, 2)}
            </pre>
            <button
              onClick={handleCopy}
              className="absolute top-2 right-2 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded transition-colors"
              title="Copy to clipboard"
            >
              📋 Copy
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
